import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import AditionalTab from "./tabs/AditionalTab";
import SpecialActionsTab from "./tabs/SpecialActionsTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const initialFormFields = {
        ROWID: '00000000-0000-0000-0000-000000000000',
        RECORD: '',
        SERIALNUMBER: '',
        PACKAGEID: '',
        CUSTOMER: '',
        ITEMCODE: '',
        LOTCODE: '',
        SUBLOTCODE: '',
        NETWEIGHT: null,
        GROSSWEIGHT: null,
        ORIGINALNETWEIGHT: null,
        ORIGINALGROSSWEIGHT: null,
        INTRANSACTIONNUM: '',
        INTRANSACTIONDT: null,
        OUTTRANSACTIONNUM: '',
        OUTTRANSACTIONDT: null,
        REFERENCE1: '',
        REFERENCE2: '',
        REFERENCE3: '',
        REFERENCE4: '',
        REFERENCE5: '',
        USERCODE: '',
        DAMAGEFLAG: '',
        DAMAGEDESCRIPTION: '',
        RECEIPTVERIFIED: '',
        SHIPMENTVERIFIED: '',
        SIZE1: null,
        SIZE2: null,
        COMMENT: '',
        BARCODELABEL: '',
    };
    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    const loadSerialNumberById = useCallback(async (rowId) => {
        if (!rowId || rowId === '00000000-0000-0000-0000-000000000000') return;

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.SerialNumberCodes.GetSerialNumber}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                const sn = response.data;
                // Server uses camelCase JSON by default (Ok(dto)) — read camelCase first,
                // fall back to PascalCase if a future endpoint returns it that way.
                setformFields({
                    // IMSerialNumbersDTO exposes Rowid (single-cap) which camelCases to "rowid".
                    // Keep "rowId" / "RowId" fallbacks for forward compatibility.
                    ROWID: sn.rowid || sn.Rowid || sn.rowId || sn.RowId || initialFormFields.ROWID,
                    RECORD: sn.recordNumber || sn.RecordNumber || '',
                    SERIALNUMBER: (sn.serialNumber || sn.SerialNumber || '').trim(),
                    PACKAGEID: (sn.packageId || sn.PackageId || '').trim(),
                    CUSTOMER: (sn.customer || sn.Customer || '').trim(),
                    ITEMCODE: (sn.itemCode || sn.ItemCode || '').trim(),
                    LOTCODE: (sn.lotCode || sn.LotCode || '').trim(),
                    SUBLOTCODE: (sn.subLotCode || sn.sublotCode || sn.SublotCode || '').trim(),
                    NETWEIGHT: sn.netWeight ?? sn.NetWeight ?? null,
                    GROSSWEIGHT: sn.grossWeight ?? sn.GrossWeight ?? null,
                    ORIGINALNETWEIGHT: sn.originalNetWeight ?? sn.OriginalNetWeight ?? null,
                    ORIGINALGROSSWEIGHT: sn.originalGrossWeight ?? sn.OriginalGrossWeight ?? null,
                    INTRANSACTIONNUM: (sn.inTransactionNum || sn.InTransactionNum || '').trim(),
                    INTRANSACTIONDT: sn.inTransactionDt ?? sn.InTransactionDt ?? null,
                    OUTTRANSACTIONNUM: (sn.outTransactionNum || sn.OutTransactionNum || '').trim(),
                    OUTTRANSACTIONDT: sn.outTransactionDt ?? sn.OutTransactionDt ?? null,
                    REFERENCE1: (sn.userDefinedReference1 || sn.UserDefinedReference1 || '').trim(),
                    REFERENCE2: (sn.userDefinedReference2 || sn.UserDefinedReference2 || '').trim(),
                    REFERENCE3: (sn.userDefinedReference3 || sn.UserDefinedReference3 || '').trim(),
                    REFERENCE4: (sn.userDefinedReference4 || sn.UserDefinedReference4 || '').trim(),
                    REFERENCE5: (sn.userDefinedReference5 || sn.UserDefinedReference5 || '').trim(),
                    USERCODE: (sn.userDefinedCode1 || sn.UserDefinedCode1 || '').trim(),
                    DAMAGEFLAG: (sn.damageFlag || sn.DamageFlag || '').trim(),
                    DAMAGEDESCRIPTION: (sn.damageDescription || sn.DamageDescription || '').trim(),
                    RECEIPTVERIFIED: (sn.receiptVerified || sn.ReceiptVerified || '').trim(),
                    SHIPMENTVERIFIED: (sn.shipmentVerified || sn.ShipmentVerified || '').trim(),
                    SIZE1: sn.size1 ?? sn.Size1 ?? null,
                    SIZE2: sn.size2 ?? sn.Size2 ?? null,
                    COMMENT: sn.comment ?? sn.Comment ?? '',
                    BARCODELABEL: (sn.barcodeLabel || sn.BarcodeLabel || '').trim(),
                });
            }
        } catch (error) {
            console.error('Error loading Serial Number:', error);
            notify('Failed to load Serial Number record.', 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadSerialNumberById(rowId);
        }
    }, [props.id, loadSerialNumberById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const payload = {
                // Property name matches IMSerialNumbersDTO.Rowid on the server.
                Rowid: formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000" ? formFields.ROWID : "00000000-0000-0000-0000-000000000000",
                KeySequence: 0,
                SerialNumber: formFields.SERIALNUMBER || "",
                UserDefinedReference1: formFields.REFERENCE1 || "",
                UserDefinedReference2: formFields.REFERENCE2 || "",
                UserDefinedReference3: formFields.REFERENCE3 || "",
                UserDefinedReference4: formFields.REFERENCE4 || "",
                UserDefinedReference5: formFields.REFERENCE5 || "",
                UserDefinedCode1: formFields.USERCODE || "",
                DamageFlag: formFields.DAMAGEFLAG || "",
                DamageDescription: formFields.DAMAGEDESCRIPTION || "",
                ReceiptVerified: formFields.RECEIPTVERIFIED || "",
                ShipmentVerified: formFields.SHIPMENTVERIFIED || "",
                Size1: formFields.SIZE1 ?? null,
                Size2: formFields.SIZE2 ?? null,
                Comment: formFields.COMMENT || "",
                BarcodeLabel: formFields.BARCODELABEL || "",
            };

            const isUpdate = formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000";
            const endpoint = isUpdate ? data.SerialNumberCodes.UpdateSerialNumber : data.SerialNumberCodes.InsertSerialNumber;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const envelope = resp?.data;
            const success = envelope?.success ?? envelope?.Success;
            if (success === false) {
                const failMsg = envelope?.message || envelope?.Message || "Failed to save Serial Number.";
                notify(failMsg, "error", 5000);
                return;
            }

            // ServiceResponse<IMSerialNumbersDTO> — saved row sits in resultdata.
            const saved = envelope?.resultdata || envelope?.Resultdata;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.rowid || saved.Rowid || saved.rowId || saved.RowId || prev.ROWID,
                    RECORD: saved.recordNumber || saved.RecordNumber || prev.RECORD,
                    SERIALNUMBER: (saved.serialNumber || saved.SerialNumber || prev.SERIALNUMBER || '').trim(),
                    REFERENCE1: (saved.userDefinedReference1 || saved.UserDefinedReference1 || prev.REFERENCE1 || '').trim(),
                    REFERENCE2: (saved.userDefinedReference2 || saved.UserDefinedReference2 || prev.REFERENCE2 || '').trim(),
                    REFERENCE3: (saved.userDefinedReference3 || saved.UserDefinedReference3 || prev.REFERENCE3 || '').trim(),
                    REFERENCE4: (saved.userDefinedReference4 || saved.UserDefinedReference4 || prev.REFERENCE4 || '').trim(),
                    REFERENCE5: (saved.userDefinedReference5 || saved.UserDefinedReference5 || prev.REFERENCE5 || '').trim(),
                    USERCODE: (saved.userDefinedCode1 || saved.UserDefinedCode1 || prev.USERCODE || '').trim(),
                    DAMAGEFLAG: (saved.damageFlag || saved.DamageFlag || prev.DAMAGEFLAG || '').trim(),
                    DAMAGEDESCRIPTION: (saved.damageDescription || saved.DamageDescription || prev.DAMAGEDESCRIPTION || '').trim(),
                    RECEIPTVERIFIED: (saved.receiptVerified || saved.ReceiptVerified || prev.RECEIPTVERIFIED || '').trim(),
                    SHIPMENTVERIFIED: (saved.shipmentVerified || saved.ShipmentVerified || prev.SHIPMENTVERIFIED || '').trim(),
                    SIZE1: saved.size1 ?? saved.Size1 ?? prev.SIZE1 ?? null,
                    SIZE2: saved.size2 ?? saved.Size2 ?? prev.SIZE2 ?? null,
                    COMMENT: saved.comment ?? saved.Comment ?? prev.COMMENT ?? '',
                    BARCODELABEL: (saved.barcodeLabel || saved.BarcodeLabel || prev.BARCODELABEL || '').trim(),
                }));
            }

            notify("Serial Number saved successfully.", "success", 3000);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                "Failed to save Serial Number.";

            notify(message, "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadSerialNumberById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push("/wm/codes/serial-number/edit");
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            Serial Number Record
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Additional">
                                    <AditionalTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Special Actions">
                                    <SpecialActionsTab
                                        formFields={formFields}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
